/**
 * "PRISM was updated" (1.2.4): after a redeploy the open app is still the old build. When the live stream connects
 * again, and when the tab is shown again (at most once a minute), the app reads the server's version; when it is not
 * the version this build is, a bar offers Reload.
 */
import { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { PRISM_VERSION } from '@prism/contracts';
import { api } from '../api/client';

const SHOWN_CHECK_EVERY_MS = 60_000;

/** The version to offer a reload to, or null while the server runs this build's version (or did not say). */
export function updatedVersion(server: string | null | undefined, own: string = PRISM_VERSION): string | null {
  return server && server !== own ? server : null;
}

export function UpdateNotice({ live }: { live: 'connecting' | 'live' | 'offline' }) {
  const [version, setVersion] = useState<string | null>(null);
  const lastCheck = useRef(0);
  const check = useRef(async () => {
    lastCheck.current = Date.now();
    try {
      const r = await api<{ version: string }>('/api/v1/version?brief');
      setVersion(updatedVersion(r.version));
    } catch {
      // Unreachable: the next reconnect asks again.
    }
  });

  // Every (re)connect of the live stream.
  useEffect(() => {
    if (live === 'live') void check.current();
  }, [live]);

  useEffect(() => {
    const shown = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastCheck.current >= SHOWN_CHECK_EVERY_MS) void check.current();
    };
    document.addEventListener('visibilitychange', shown);
    return () => document.removeEventListener('visibilitychange', shown);
  }, []);

  if (!version) return null;
  return (
    <div className="offline-bar update-bar" role="status" data-testid="update-notice">
      <RefreshCw size={14} /> PRISM was updated to {version}.
      <button className="btn sm" onClick={() => window.location.reload()} data-testid="update-reload">Reload</button>
    </div>
  );
}
